import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { ArrowDownIcon } from "@src/assets/general-icons";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { purchasingActions, systemActions } from "@src/store";
import { clx } from "../utils/stringUtils";
import styles from "./BulkActionsMenu.module.scss";

export type TBulkMenuItem = {
	id: number;
	label: string;
	onClick: () => void;
};

type TBulkActionsMenuProps = {
	items: Array<TBulkMenuItem>;
	onMarkNotInHand: () => void;
	isDisabled?: boolean;
	onOpenClick?: () => void;
};

const BulkActionsMenu: React.FC<TBulkActionsMenuProps> = ({ items, onMarkNotInHand, isDisabled = false, onOpenClick }) => {
	const dispatch = useAppDispatch();
	const selectedListings = useAppSelector(selectSelectedInventoryItems);
	const theme = useAppSelector((state) => state.settings.theme);
	const locations = useAppSelector((state) => state.system.locations.data);
	const locationsInit = useAppSelector((state) => state.system.locations.init);
	const locationsLoading = useAppSelector((state) => state.system.locations.loading);

	const triggerRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [open, setOpen] = useState(false);
	const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
	const [submenuLeft, setSubmenuLeft] = useState(false);
	const [submenuOpen, setSubmenuOpen] = useState(false);
	const [loadingMethodId, setLoadingMethodId] = useState<number | null>(null);

	const deliveryMethods = locations.filter((l) => l.Location_ID != null && l.Location_Name && l.Location_Name.trim().length > 0);

	useEffect(() => {
		if (!open || locationsInit || locationsLoading) return;
		dispatch(systemActions.listLocations({ _dc: String(Date.now()) }));
	}, [open, dispatch, locationsInit, locationsLoading]);

	const updatePosition = useCallback(() => {
		const trigger = triggerRef.current?.getBoundingClientRect();
		const panel = panelRef.current?.getBoundingClientRect();
		if (!trigger || !panel) return;

		const margin = 10;
		const gap = 6;

		let top = trigger.bottom + gap;
		let left = trigger.left;

		if (top + panel.height + margin > window.innerHeight) {
			const upTop = trigger.top - gap - panel.height;
			top = upTop >= margin ? upTop : Math.max(margin, window.innerHeight - panel.height - margin);
		}

		if (left + panel.width + margin > window.innerWidth) {
			left = Math.max(margin, window.innerWidth - panel.width - margin);
		}

		setSubmenuLeft(left + panel.width + 190 > window.innerWidth);
		setCoords({ top, left });
	}, []);

	useLayoutEffect(() => {
		if (!open) {
			setCoords(null);
			setSubmenuOpen(false);
			return;
		}
		updatePosition();
	}, [open, updatePosition]);

	useEffect(() => {
		if (!open) return;
		const handler = () => updatePosition();
		window.addEventListener("resize", handler);
		window.addEventListener("scroll", handler, true);
		return () => {
			window.removeEventListener("resize", handler);
			window.removeEventListener("scroll", handler, true);
		};
	}, [open, updatePosition]);

	useEffect(() => {
		if (!open) return;
		const handleDown = (event: MouseEvent) => {
			const target = event.target as Node;
			if (triggerRef.current?.contains(target)) return;
			if (panelRef.current?.contains(target)) return;
			setOpen(false);
		};
		document.addEventListener("mousedown", handleDown);
		return () => document.removeEventListener("mousedown", handleDown);
	}, [open]);

	const handleToggle = () => {
		if (isDisabled) return;
		if (!open) onOpenClick?.();
		setOpen((prev) => !prev);
	};

	const cancelSubmenuClose = () => {
		if (submenuCloseTimerRef.current) {
			clearTimeout(submenuCloseTimerRef.current);
			submenuCloseTimerRef.current = null;
		}
	};

	const openSubmenu = () => {
		cancelSubmenuClose();
		setSubmenuOpen(true);
	};

	const scheduleSubmenuClose = () => {
		cancelSubmenuClose();
		submenuCloseTimerRef.current = setTimeout(() => setSubmenuOpen(false), 150);
	};

	const handleMarkInHand = async (locationId: number) => {
		if (!selectedListings.length) return;
		setLoadingMethodId(locationId);
		try {
			await dispatch(
				purchasingActions.updateListingInHand({
					ListingIds: selectedListings.map((x) => x.Listing_ID),
					InHand: true,
					LocationId: locationId,
				}),
			).unwrap();
			toast.success("Marked tickets as In-Hand.");
			setOpen(false);
		} catch {
			toast.error("Failed to mark tickets as In-Hand.");
		} finally {
			setLoadingMethodId(null);
		}
	};

	const handleItemClick = (onClick: () => void) => {
		setOpen(false);
		onClick();
	};

	return (
		<>
			<div ref={triggerRef} className={styles.triggerWrap}>
				<Button variant={ButtonVariant.Secondary} disabled={isDisabled} onClick={handleToggle}>
					<span className={styles.triggerInner}>
						<span>Bulk Actions</span>
						<ArrowDownIcon />
					</span>
				</Button>
			</div>
			{open &&
				createPortal(
					<div className={`theme--${theme}`}>
						<div
							ref={panelRef}
							className={styles.menuPanel}
							style={{
								top: coords?.top ?? 0,
								left: coords?.left ?? 0,
								visibility: coords ? "visible" : "hidden",
							}}>
							<div
								className={styles.submenuRoot}
								onMouseEnter={openSubmenu}
								onMouseLeave={scheduleSubmenuClose}>
								<Button
									variant={ButtonVariant.Borderless}
									controlSize="xsm"
									disabled={loadingMethodId !== null}
									onClick={() => setSubmenuOpen((prev) => !prev)}>
									<div className={styles.submenuTrigger}>
										<span>Mark Tickets In Hand</span>
										<span className={styles.chevron}>›</span>
									</div>
								</Button>
								{submenuOpen && (
									<div
										className={clx(styles.submenuPanel, submenuLeft ? styles.submenuPanelLeft : "")}
										onMouseEnter={openSubmenu}
										onMouseLeave={scheduleSubmenuClose}>
										{!deliveryMethods.length ? (
											<div className={styles.loadingText}>
												{locationsLoading ? "Loading…" : "No delivery methods"}
											</div>
										) : (
											deliveryMethods.map((loc) => (
												<Button
													key={loc.Location_ID ?? loc.Location_Name}
													variant={ButtonVariant.Borderless}
													controlSize="xsm"
													isLoading={loadingMethodId === loc.Location_ID}
													disabled={loadingMethodId !== null}
													onClick={() => loc.Location_ID != null && handleMarkInHand(loc.Location_ID)}>
													{loc.Location_Name.trim()}
												</Button>
											))
										)}
									</div>
								)}
							</div>
							<Button
								variant={ButtonVariant.Borderless}
								controlSize="xsm"
								onClick={() => handleItemClick(onMarkNotInHand)}>
								Mark Tickets Not In Hand
							</Button>
							{items.map((item) => (
								<Button
									key={item.id}
									variant={ButtonVariant.Borderless}
									controlSize="xsm"
									onClick={() => handleItemClick(item.onClick)}>
									{item.label}
								</Button>
							))}
						</div>
					</div>,
					document.body,
				)}
		</>
	);
};

export default BulkActionsMenu;
